import { AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

import { useApiKeySettings, API_KEY_PAGE_SIZE } from "@/hooks/use-api-key-settings";
import type { ApiKey, ApiKeyWithOwner } from "@/api/generated/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { env } from "@/config/env";
import { useAuth } from "@/hooks/use-auth";

import { ApiKeyCreateForm } from "./ApiKeyCreateForm";
import { ApiKeyListItem } from "./ApiKeyListItem";
import { GeneratedKeyCard } from "./GeneratedKeyCard";

const DOCS_URL = `${env.apiBaseUrl}/api/v1/docs`;

export function ApiKeySettings() {
  const {
    name,
    setName,
    selectedScopes,
    toggleReadScopes,
    toggleWriteScopes,
    expiresAt,
    setExpiresAt,
    generatedKey,
    error,
    isLoading,
    isApiKeysError,
    refetch,
    apiKeys,
    apiKeysTotal,
    page,
    setPage,
    isCompanyAdmin,
    companyApiKeys,
    companyApiKeysTotal,
    companyPage,
    setCompanyPage,
    isCompanyLoading,
    isCompanyApiKeysError,
    refetchCompany,
    currentUserId,
    createDisabled,
    isCreating,
    handleSubmit,
    handleRevoke,
    revokingKeyId,
  } = useApiKeySettings();
  const { user: authenticatedUser } = useAuth();

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | ApiKeyWithOwner | null>(null);

  const getOwnerName = (apiKey: ApiKey | ApiKeyWithOwner) =>
    isCompanyAdmin && "ownerName" in apiKey && apiKey.userId !== currentUserId
      ? String(apiKey.ownerName)
      : authenticatedUser?.name;

  const visibleApiKeys = isCompanyAdmin ? companyApiKeys : apiKeys;
  const isVisibleKeysLoading = isCompanyAdmin ? isCompanyLoading : isLoading;
  const visibleKeysError = isCompanyAdmin ? isCompanyApiKeysError : isApiKeysError;
  const retryVisibleKeys = isCompanyAdmin ? refetchCompany : refetch;
  const visibleKeysTotal = isCompanyAdmin ? companyApiKeysTotal : apiKeysTotal;
  const visibleKeysPage = isCompanyAdmin ? companyPage : page;
  const setVisibleKeysPage = isCompanyAdmin ? setCompanyPage : setPage;
  const visibleKeysPageCount = Math.max(1, Math.ceil(visibleKeysTotal / API_KEY_PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
            <div>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>
                Send data into TurboClaim from other platforms. Keys act on your behalf and only
                allow the scopes you select.{" "}
                <a
                  href={DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 underline underline-offset-2"
                >
                  API documentation <ExternalLink className="h-3 w-3" />
                </a>
              </CardDescription>
            </div>
            <Button
              type="button"
              onClick={() => setIsCreateFormOpen((prev) => !prev)}
              aria-expanded={isCreateFormOpen}
              aria-controls="create-api-key-form"
              className="max-sm:w-full"
            >
              {isCreateFormOpen ? "Hide form" : "New API key"}
            </Button>
          </div>
        </CardHeader>
        {isCreateFormOpen && (
          <CardContent id="create-api-key-form">
            <ApiKeyCreateForm
              name={name}
              setName={setName}
              selectedScopes={selectedScopes}
              toggleReadScopes={toggleReadScopes}
              toggleWriteScopes={toggleWriteScopes}
              expiresAt={expiresAt}
              setExpiresAt={setExpiresAt}
              createDisabled={createDisabled}
              isCreating={isCreating}
              onSubmit={async (event) => {
                // Hide the form on success so the one-time generated key is visible.
                if (await handleSubmit(event)) {
                  setIsCreateFormOpen(false);
                }
              }}
            />
          </CardContent>
        )}
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {generatedKey && <GeneratedKeyCard generatedKey={generatedKey} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Keys</CardTitle>
          <CardDescription>
            {isCompanyAdmin
              ? "All active keys in your company. You can revoke any of them."
              : "Keys you have created. Revoking is immediate and permanent."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isVisibleKeysLoading ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading API keys...
            </div>
          ) : visibleKeysError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Unable to load API keys.</span>
                <Button type="button" variant="outline" size="sm" onClick={() => void retryVisibleKeys()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : visibleApiKeys.length === 0 ? (
            <p className="text-sm text-muted-foreground">No API keys yet.</p>
          ) : (
            <>
              {visibleApiKeys.map((apiKey) => (
                <ApiKeyListItem
                  key={apiKey.id}
                  apiKey={apiKey}
                  ownerName={getOwnerName(apiKey)}
                  isRevoking={revokingKeyId === apiKey.id}
                  onRevoke={() => setRevokeTarget(apiKey)}
                />
              ))}
              {visibleKeysPageCount > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-muted-foreground">
                    Page {visibleKeysPage} of {visibleKeysPageCount} ({visibleKeysTotal} keys)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={visibleKeysPage <= 1}
                      onClick={() => setVisibleKeysPage(visibleKeysPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={visibleKeysPage >= visibleKeysPageCount}
                      onClick={() => setVisibleKeysPage(visibleKeysPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget
                ? `"${revokeTarget.name}" will stop working immediately. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revokeTarget?.id != null) {
                  void handleRevoke(revokeTarget.id);
                }
                setRevokeTarget(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
